import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import classNames from 'classnames';
import { useInView } from 'react-intersection-observer';
import { isCrawler, isCurrentUserPremium, isProduction, isStaging } from 'helpers/authentication';
import { useWindowSize } from 'helpers/useWindowSize';
import AdmobBanner from './AdmobBanner';
import Analytics from './Analytics';

/*
masthead e footer: questi div sono da inserire su tutte le pagine, ricordandoti che sono banner ancorati.
Il footer puoi decidere di ancorarlo sia sopra che sotto al menù, il masthead ti consiglierei di farlo ancorare sopra la barra di ricerca.
spalle: sono i banner posizionati a lato dell'articolo, faremo in modo che si ancorino per qualche secondo in modo che la viewability sia alta,
da inserire sul desktop e tablet landscape
article: questi potrai inserirli all'interno di articoli ed homepage,
il top deve essere inserito all'inizio dell'articolo, ed il middle dopo qualche paragrafo.
Il nostro consiglio è di inserire il bottom tra il penultimo e ultimo paragrafo, e non mettere niente dopo la fine dell'articolo
// dentro il body
<div id="adk_masthead"></div>
<div id="adk_footer"></div>
// in spalla
<div id="adk_spalla-top"></div>
<div id="adk_spalla-middle"></div>
<div id="adk_spalla-bottom"></div>
// dentro l'articolo
<div id="adk_article-top"></div>
<div id="adk_article-middle"></div>
<div id="adk_article-bottom"></div>
*/

export enum AdSlot {
  HEADER = 'adk_masthead',
  FOOTER = 'adk_footer',
  SPALLA_TOP = 'adk_spalla-top',
  SPALLA_MIDDLE = 'adk_spalla-middle',
  SPALLA_BOTTOM = 'adk_spalla-bottom',
  ARTICLE_TOP = 'adk_article-top',
  ARTICLE_MIDDLE = 'adk_article-middle',
  ARTICLE_BOTTOM = 'adk_article-bottom',
}

export const AdKaoraBanner: React.FC<{ slot: AdSlot }> = ({ slot }) => {
  const size = useWindowSize();
  const isMobileOnly = size.width < 768;
  const { ref, inView, entry } = useInView({
    threshold: 1, // fully visible
    triggerOnce: true, // Only trigger the callback once
  });

  useEffect(() => {
    const onImpression = (slot: AdSlot) => {
      Analytics.sendEvent({
        category: 'Banner Impression',
        action: 'Impression',
        label: slot,
      });
    };

    if (inView && entry) {
      onImpression(slot);
    }
  }, [inView, slot, entry]);

  let banner = <div id={slot} ref={ref} />;

  //if is localhost
  if (!isProduction() && !isStaging()) {
    banner = (
      <img
        src='/images/banner_placeholder.jpg'
        alt='placeholder'
        className={classNames(
          'w-full object-cover',
          {
            'h-48':
              slot === AdSlot.HEADER ||
              slot === AdSlot.FOOTER ||
              slot === AdSlot.ARTICLE_TOP ||
              slot === AdSlot.ARTICLE_MIDDLE ||
              slot === AdSlot.ARTICLE_BOTTOM,
          },
          {
            'w-[300px]':
              slot === AdSlot.SPALLA_TOP ||
              slot === AdSlot.SPALLA_MIDDLE ||
              slot === AdSlot.SPALLA_BOTTOM ||
              slot === AdSlot.ARTICLE_TOP ||
              slot === AdSlot.ARTICLE_MIDDLE ||
              slot === AdSlot.ARTICLE_BOTTOM,
          },
        )}
      />
    );
  }

  if (isCurrentUserPremium() || isCrawler()) {
    return null;
  }

  return Capacitor.isNativePlatform() ? (
    <AdmobBanner />
  ) : (
    <div
      className={classNames(
        'flex w-full max-w-full justify-center overflow-hidden',
        {
          'max-h-[400px] min-h-64 min-w-[300px]':
            (slot === AdSlot.ARTICLE_TOP || slot === AdSlot.ARTICLE_MIDDLE || slot === AdSlot.ARTICLE_BOTTOM) &&
            !isMobileOnly,
        },
        {
          'max-h-[600px] min-h-32':
            (slot === AdSlot.ARTICLE_TOP || slot === AdSlot.ARTICLE_MIDDLE || slot === AdSlot.ARTICLE_BOTTOM) &&
            isMobileOnly,
        },
        {
          'max-h-[600px] min-h-32 min-w-[300px]':
            slot === AdSlot.SPALLA_BOTTOM || slot === AdSlot.SPALLA_MIDDLE || slot === AdSlot.SPALLA_TOP,
        },
        {
          'max-h-32 min-h-20': slot === AdSlot.FOOTER,
        },
        {
          'max-h-64 min-h-32': slot === AdSlot.HEADER && !isMobileOnly,
        },
        {
          'max-h-32 min-h-28': slot === AdSlot.HEADER && isMobileOnly,
        },
      )}
    >
      {banner}
    </div>
  );
};
export default AdKaoraBanner;
